import { FC } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
  Box,
  Button,
  IconStyled,
  TextFieldStyled,
  inputMediumStyles,
  MenuItem,
  ButtonAppearance,
  ButtonSize,
  ButtonProps,
} from '@bechtel/gds';
import { SearchField } from '@bechtel/client-common';
export const listWidth = '392px';
export const desktopScreenListWidth = '300px';
export const smallScreenListWidth = '286px';
export const menuHeight = '205px';
export const searchMenuHeight = '130px';
export const menuWidth = '230px';
export const searchMenuWidth = '120px';
export const smallSearchMenuWidth = '100px';
export const smallScreenMenuWidth = '210px';
export const labelWidth = '100px';
export const smallScreenLabelWidth = '60px';

export const RodRequestListContainerStyled = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  height: 100vh;
  left: 0;
  position: relative;
  top: 0;
  width: ${listWidth};
  /* Z-index needed to show box shadow above rod request detail */
  z-index: 1;
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.widescreen}) {
    width: ${smallScreenListWidth};
  }
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: ${desktopScreenListWidth};
  }
  box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14);
  color: ${({ theme }) => theme.colors.grey[1000]};
  &::before {
    box-shadow: 14px 0 12px -8px rgba(0, 0, 0, 0.1);
    content: '';
    height: 100%;
    pointer-events: none;
    position: absolute;
    right: 0;
    width: 32px;
  }
`;

export const ArcStrykerLogoStyled = styled(Box).attrs({
  py: { sm: '450' },
  pr: { sm: '550' },
  pl: { sm: '500' },
  mr: { sm: '450' },
})`
  ${({ theme }) => css`
    background-image: linear-gradient(225deg, ${theme.colors.grey[200]}, ${theme.colors.grey[100]});
  `}
`;

export const RodRequestListHeaderStyled = styled(Box).attrs({
  flexDirection: { sm: 'column' },
})`
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
`;

export const SearchFieldStyled = styled(Box).attrs({
  mb: { sm: '400' },
  alignItems: { sm: 'center' },
  justifyContent: { sm: 'center' },
  px: { sm: '500' },
})`
  ${IconStyled} {
    right: ${({ theme }) => theme.spacings[500]};
  }
  ${TextFieldStyled} {
    ${({ theme }) => css`
      font-size: ${theme.fontSizes[400]};
      border-color: ${theme.colors.grey[300]};
      font-family: 'Graphik';
    `}
  }
`;

export const ListStyled = styled(Box).attrs({
  flexDirection: { sm: 'column' },
  flexGrow: { sm: '1' },
})`
  overflow-y: auto;
`;

export const NavLinkStyled = styled(NavLink)`
  ${({ theme }) => css`
    border-radius: ${theme.radii[200]};
    border-bottom: 1px solid ${theme.colors.grey[300]};
    color: inherit;
    display: block;
    padding-top: ${theme.spacings[450]};
    padding-left: ${theme.spacings[400]};
    padding-right: ${theme.spacings[400]};
    padding-bottom: ${theme.spacings[450]};
    position: relative;
    text-decoration: none;
    transition-timing-function: ease-in-out;
    transition-duration: 80ms;
    transition-property: background-color;
    @media only screen and (max-width: ${theme.breakpoints.widescreen}) {
      padding-top: ${theme.spacings[450]};
      padding-bottom: ${theme.spacings[450]};
    }
    &:active,
    &:hover,
    &&.active {
      background-color: ${theme.colors.bechtel.blue[100]};
    }
  `}
`;

export const AddRodRequestButtonStyled = styled(Button).attrs({
  appearance: ButtonAppearance.TERTIARY,
  size: ButtonSize.SMALL,
})`
  width: auto;
  ${({ theme }) => css`
    padding: ${theme.spacings[450]};
    pointer-events: all;
    height: ${theme.spacings['700']};
    background-color: ${theme.colors.grey[100]};
    opacity: 1;
    align-items: center;
    &:focus {
      box-shadow: none;
    }
  `}
`;
export const PopperContainerStyled = styled(Box).attrs({
  alignItems: { sm: 'baseline' },
})`
  ${({ theme }) => css`
    border-radius: ${theme.spacings[200]};
    position: absolute;
    z-index: 1;
    top: ${theme.spacings[700]};
  `}
`;

export const MenuItemContainerStyled = styled(Box).attrs({
  px: { sm: '200' },
})`
  min-width: ${menuWidth};
  max-height: ${menuHeight};
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.widescreen}) {
    min-width: ${smallScreenMenuWidth};
  }
`;

export const SearchMenuItemContainerStyled = styled(Box).attrs({
  px: { sm: '200' },
})`
  min-width: ${searchMenuWidth};
  max-height: ${searchMenuHeight};
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.widescreen}) {
    width: ${smallSearchMenuWidth};
  }
`;

export const MenuItemStyled = styled(MenuItem)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes[400]};
    color: ${theme.colors.grey[1000]};
    white-space: nowrap;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.grey[300]};
    }
  `}
`;
export const SearchMenuItemStyled = styled(MenuItem)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes[400]};
    padding-left: ${theme.spacings[400]};
    padding-top: ${theme.spacings[400]};
    padding-bottom: ${theme.spacings[400]};
    padding-right: ${theme.spacings[400]};
    color: ${theme.colors.grey[1000]};
    white-space: nowrap;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.grey[300]};
    }
  `}
`;

export const SearchFieldComponentStyled = styled(SearchField)`
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.widescreen}) {
    ${inputMediumStyles}
  }
`;
export interface TabButtonProps extends ButtonProps {
  selected: boolean;
}
export const TabButtonStyled = styled(Button as FC<TabButtonProps>).attrs({
  appearance: ButtonAppearance.TERTIARY,
  size: ButtonSize.LARGE,
})`
  width: auto;
  border: none;
  border-radius: 0;
  position: relative;
  &:focus {
    border-color: transparent;
    box-shadow: none;
  }
  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    color: ${theme.colors.grey[1000]};
    font-size: ${theme.fontSizes[300]};
    padding-left: ${theme.spacings[700]};
    padding-right: ${theme.spacings[700]};
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 5px;
      background-color: ${theme.colors.blue[700]};
      opacity: 0;
    }
  `};
  ${(props) =>
    props.selected &&
    css`
      font-weight: ${props.theme.fontWeights[600]};
      &::after {
        opacity: 1;
      }
    `};
`;

export const FilterContainerStyled = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 33% 33% 34%;
    padding-top: ${theme.spacings[200]};
    padding-bottom: ${theme.spacings[200]};
    padding-left: ${theme.spacings[450]};
    padding-right: ${theme.spacings[450]};
  `}
`;

export const LabelStyled = styled(Box)`
  width: ${labelWidth};
  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.widescreen}) {
    width: ${smallScreenLabelWidth};
  }
`;
